import { StripeMetadataInput } from '../../components/VaultForm';
import { StripeEnv } from '../../util/enums';
import logger from '../../util/logger';
import { StripeError } from './errors';
import { vaultRequest } from './request';
import {
  createStripePerson,
  getInvalidRepresentativeId,
  updateStripePerson,
} from './stripePerson';

const CREATE_STRIPE_CONNECT_ACCOUNT_MUTATION = `
mutation VaultStripeConnectAccountCreate(
  $vaultKybId: ID!,
  $statementDescriptor: String,
  $metadata: StripeMetadataInput
  $stripeEnv: VaultStripeEnv
  ) {
  stripeConnectAccountId: vaultStripeConnectAccountCreate(
    vaultKybId: $vaultKybId,
    statementDescriptor: $statementDescriptor,
    metadata: $metadata
    stripeEnv: $stripeEnv
    )
}
`;

const UPDATE_STRIPE_CONNECT_ACCOUNT_MUTATION = `
mutation VaultStripeConnectAccountUpdate(
  $stripeConnectAccountId: String!,
  $vaultKybId: ID!,
  $statementDescriptor: String,
  $metadata: StripeMetadataInput
  $stripeEnv: VaultStripeEnv
  ) {
  stripeConnectAccountId: vaultStripeConnectAccountUpdate(
    stripeConnectAccountId: $stripeConnectAccountId,
    vaultKybId: $vaultKybId,
    statementDescriptor: $statementDescriptor,
    metadata: $metadata
    stripeEnv: $stripeEnv
  )
}
`;

const REMOVE_ACCOUNT_REPRESENTATIVE_MUTATION = `
mutation VaultStripeConnectAccountRemoveRepresentative(
  $stripeConnectAccountId: String!,
  $stripeEnv: VaultStripeEnv
  ) {
  vaultStripeConnectAccountRemoveRepresentative(
    stripeConnectAccountId: $stripeConnectAccountId,
    stripeEnv: $stripeEnv
    ) {
    success
    errors {
      message
      type
    }
  }
}`;

const STRIPE_CONNECT_ACCOUNT_OWNERS_PROVIDED = `
mutation VaultStripeConnectAccountOwnersProvided(
  $stripeConnectAccountId: String!,
  $ownersProvided: Boolean!,
  $stripeEnv: VaultStripeEnv
  ) {
  vaultStripeConnectAccountOwnersProvided(
    stripeConnectAccountId: $stripeConnectAccountId,
    ownersProvided: $ownersProvided,
    stripeEnv: $stripeEnv
    )
}`;

export const createStripeConnectAccount = async (
  vaultKybId: string,
  statementDescriptor?: string,
  metadata?: StripeMetadataInput,
  stripeEnv?: StripeEnv,
): Promise<string> => {
  const stripeConnectAccountIdRes = await vaultRequest(
    CREATE_STRIPE_CONNECT_ACCOUNT_MUTATION,
    {
      vaultKybId,
      statementDescriptor,
      metadata,
      stripeEnv,
    },
  );
  const stripeConnectAccountIdResJson = await stripeConnectAccountIdRes.json();
  if (
    stripeConnectAccountIdResJson.errors &&
    stripeConnectAccountIdResJson.errors.length > 0
  ) {
    throw new StripeError(
      'Could not create Stripe Connect Account',
      stripeConnectAccountIdResJson.errors[0],
    );
  }
  return stripeConnectAccountIdResJson.data.stripeConnectAccountId;
};

export const updateStripeConnectAccount = async (
  stripeConnectAccountId: string,
  vaultKybId: string,
  statementDescriptor?: string,
  metadata?: StripeMetadataInput,
  stripeEnv?: StripeEnv,
): Promise<string> => {
  const stripeConnectAccountIdRes = await vaultRequest(
    UPDATE_STRIPE_CONNECT_ACCOUNT_MUTATION,
    {
      stripeConnectAccountId,
      vaultKybId,
      statementDescriptor,
      metadata,
      stripeEnv,
    },
  );
  const stripeConnectAccountIdResJson = await stripeConnectAccountIdRes.json();
  if (
    stripeConnectAccountIdResJson.errors &&
    stripeConnectAccountIdResJson.errors.length > 0
  ) {
    throw new StripeError(
      'Could not update Stripe Connect Account',
      stripeConnectAccountIdResJson.errors[0],
    );
  }
  return stripeConnectAccountIdResJson.data.stripeConnectAccountId;
};

export const removeAccountRepresentative = async (
  stripeConnectAccountId: string,
  stripeEnv?: StripeEnv,
): Promise<void> => {
  await vaultRequest(REMOVE_ACCOUNT_REPRESENTATIVE_MUTATION, {
    stripeConnectAccountId,
    stripeEnv,
  });
};

export const replaceAccountRepresentative = async (
  stripeConnectAccountId: string,
  kycId: string,
  stripeEnv?: StripeEnv,
): Promise<string> => {
  const invalidRepresentativeId = await getInvalidRepresentativeId(
    stripeConnectAccountId,
    stripeEnv,
  );
  let personId: string;
  if (invalidRepresentativeId) {
    // invalid representative can't be removed
    // update it with new data
    logger(
      `Stripe account ${stripeConnectAccountId} as an invalid representative. Replacing representative data.`,
    );
    personId = invalidRepresentativeId;
    await updateStripePerson(
      kycId,
      stripeConnectAccountId,
      invalidRepresentativeId,
      stripeEnv,
    );
  } else {
    // representative can be removed
    logger(
      `Stripe account ${stripeConnectAccountId} has valid user. Can be removed and replaced.`,
    );
    await removeAccountRepresentative(stripeConnectAccountId, stripeEnv);
    personId = await createStripePerson(
      kycId,
      stripeConnectAccountId,
      stripeEnv,
    );
  }
  return personId;
};

export const updateStripeConnectAccountOwnersProvided = async (
  stripeConnectAccountId: string,
  ownersProvided: boolean,
  stripeEnv?: StripeEnv,
): Promise<string> => {
  const updateStripeConnectAccountOwnersRes = await vaultRequest(
    STRIPE_CONNECT_ACCOUNT_OWNERS_PROVIDED,
    {
      stripeConnectAccountId,
      stripeEnv,
      ownersProvided,
    },
  );
  const updateStripeConnectAccountOwnersResJson =
    await updateStripeConnectAccountOwnersRes.json();
  if (
    updateStripeConnectAccountOwnersResJson.errors &&
    updateStripeConnectAccountOwnersResJson.errors.length > 0
  ) {
    throw new StripeError(
      'Could not update Stripe Connect Account Owners Provided',
      updateStripeConnectAccountOwnersResJson.errors[0],
    );
  }
  return updateStripeConnectAccountOwnersResJson.data
    .vaultStripeConnectAccountOwnersProvided;
};
